export interface FieldConfig {
  key: string;
  label: string;
  multiline?: boolean;
  isArray?: boolean;
}

export interface ActionTemplateConfig {
  displayName: string;
  fields: FieldConfig[];
}

export type ActionsTemplatesType = Record<string, ActionTemplateConfig>;

// Shared OData-style query fields — identical across every Dynamics 365 list tool.
const MICROSOFT_D365_QUERY_FIELDS: FieldConfig[] = [
  { key: 'filter', label: 'Filter (OData v4 expression, e.g. eq, ne, contains)' },
  { key: 'orderby', label: 'Order By (e.g. "field asc" or "field1 desc, field2")' },
  { key: 'select', label: 'Select (comma-separated field list)' },
  { key: 'top', label: 'Top (page size, default/recommended 10)' },
  { key: 'skip', label: 'Skip (pagination offset)' },
  { key: 'result_format', label: 'Result Format (text or resource)' }
];

export const ActionsTemplates: ActionsTemplatesType = {
  // Outlook Email Actions
  outlook_email__create_draft: {
    displayName: 'Creating Draft via Outlook',
    fields: [
      { key: 'body', label: 'Body', multiline: true },
      { key: 'subject', label: 'Subject' },
      { key: 'to_recipients', label: 'To Recipients', isArray: true },
      { key: 'cc_recipients', label: 'Cc Recipients', isArray: true },
      { key: 'bcc_recipients', label: 'Bcc Recipients', isArray: true }
    ]
  },
  outlook_email__send_mail: {
    displayName: 'Sending Email via Outlook',
    fields: [
      { key: 'subject', label: 'Subject' },
      { key: 'body', label: 'Body', multiline: true },
      { key: 'to_recipients', label: 'To Recipients', isArray: true },
      { key: 'cc_recipients', label: 'Cc Recipients', isArray: true },
      { key: 'bcc_recipients', label: 'Bcc Recipients', isArray: true }
    ]
  },
  outlook_email__search_email_by_content: {
    displayName: 'Searching Emails by Content via Outlook',
    fields: [
      { key: 'search_query', label: 'Search Query' },
      { key: 'subject_contains', label: 'Subject Contains' },
      { key: 'sender_email', label: 'Sender Email' },
      { key: 'max_results', label: 'Max Results' }
    ]
  },
  outlook_email__search_email_by_sender: {
    displayName: 'Searching Emails by Sender via Outlook',
    fields: [
      { key: 'sender_email', label: 'Sender Email' },
      { key: 'subject_contains', label: 'Subject Contains' },
      { key: 'max_results', label: 'Max Results' }
    ]
  },

  // Outlook Calendar Actions
  outlook_calendar__get_todays_events: {
    displayName: "Getting Today's Events via Outlook Calendar",
    fields: [{ key: 'max_results', label: 'Max Results' }]
  },
  outlook_calendar__get_date_events: {
    displayName: 'Getting Events by Date via Outlook Calendar',
    fields: [
      { key: 'target_date', label: 'Target Date' },
      { key: 'max_results', label: 'Max Results' }
    ]
  },
  outlook_calendar__create_event: {
    displayName: 'Creating Event via Outlook Calendar',
    fields: [
      { key: 'subject', label: 'Subject' },
      { key: 'start_datetime', label: 'Start DateTime' },
      { key: 'end_datetime', label: 'End DateTime' },
      { key: 'body', label: 'Notes', multiline: true },
      { key: 'location', label: 'Location' },
      { key: 'required_attendees', label: 'Required Attendees', isArray: true },
      { key: 'optional_attendees', label: 'Optional Attendees', isArray: true }
    ]
  },
  outlook_calendar__update_event: {
    displayName: 'Updating Event via Outlook Calendar',
    fields: [
      { key: 'event_id', label: 'Event ID' },
      { key: 'subject', label: 'Subject' },
      { key: 'start_datetime', label: 'Start DateTime' },
      { key: 'end_datetime', label: 'End DateTime' },
      { key: 'body', label: 'Notes', multiline: true },
      { key: 'location', label: 'Location' }
    ]
  },
  outlook_calendar__get_event: {
    displayName: 'Getting Event via Outlook Calendar',
    fields: [{ key: 'event_id', label: 'Event ID' }]
  },
  outlook_calendar__delete_event: {
    displayName: 'Deleting Event via Outlook Calendar',
    fields: [{ key: 'event_id', label: 'Event ID' }]
  },
  outlook_calendar__calendars_list_events: {
    displayName: 'Listing Events via Outlook Calendar',
    fields: [
      { key: 'calendar_id', label: 'Calendar ID' },
      { key: 'start_date', label: 'Start Date' },
      { key: 'end_date', label: 'End Date' },
      { key: 'max_results', label: 'Max Results' }
    ]
  },

  // Gmail Actions
  gmail__send_email: {
    displayName: 'Sending Email via Gmail',
    fields: [
      { key: 'mail_subject', label: 'Subject' },
      { key: 'mail_body', label: 'Body', multiline: true },
      { key: 'mail_recipient', label: 'Recipients', isArray: true },
      { key: 'cc', label: 'Cc', isArray: true },
      { key: 'bcc', label: 'Bcc', isArray: true }
    ]
  },
  gmail__search_emails: {
    displayName: 'Searching Emails via Gmail',
    fields: [
      { key: 'query', label: 'Search Query' },
      { key: 'max_results', label: 'Max Results' }
    ]
  },
  gmail__reply_to_email: {
    displayName: 'Replying to Email via Gmail',
    fields: [
      { key: 'mail_subject', label: 'Subject' },
      { key: 'mail_body', label: 'Body', multiline: true },
      { key: 'thread_id', label: 'Thread ID' },
      { key: 'mail_recipient', label: 'Recipients', isArray: true },
      { key: 'cc', label: 'Cc', isArray: true },
      { key: 'bcc', label: 'Bcc', isArray: true }
    ]
  },
  gmail__create_draft: {
    displayName: 'Creating Draft via Gmail',
    fields: [
      { key: 'mail_recipient', label: 'Recipients', isArray: true },
      { key: 'mail_subject', label: 'Subject' },
      { key: 'mail_body', label: 'Body', multiline: true },
      { key: 'cc', label: 'Cc', isArray: true },
      { key: 'bcc', label: 'Bcc', isArray: true }
    ]
  },
  gmail__create_draft_reply: {
    displayName: 'Creating Draft Reply via Gmail',
    fields: [
      { key: 'reply_content', label: 'Reply Content', multiline: true },
      { key: 'thread_id', label: 'Thread ID' },
      { key: 'to', label: 'To', isArray: true },
      { key: 'cc', label: 'Cc', isArray: true },
      { key: 'bcc', label: 'Bcc', isArray: true }
    ]
  },
  gmail__get_email_with_attachments: {
    displayName: 'Getting Email with Attachments via Gmail',
    fields: [
      { key: 'thread_id', label: 'Thread ID' },
      { key: 'message_id', label: 'Message ID' }
    ]
  },
  gmail__search_people: {
    displayName: 'Searching People via Gmail',
    fields: [
      { key: 'query', label: 'Search Query' },
      { key: 'page_size', label: 'Page Size' }
    ]
  },

  // Google Calendar Actions
  gmail__list_calendars: {
    displayName: 'Listing Calendars via Google Calendar',
    fields: []
  },
  gmail__view_events: {
    displayName: 'Viewing Events via Google Calendar',
    fields: [
      { key: 'calendar_id', label: 'Calendar ID' },
      { key: 'max_results', label: 'Max Results' },
      { key: 'time_min', label: 'Start Time' },
      { key: 'time_max', label: 'End Time' }
    ]
  },
  gmail__create_event: {
    displayName: 'Creating Event via Google Calendar',
    fields: [
      { key: 'title', label: 'Title' },
      { key: 'start_time', label: 'Start Time' },
      { key: 'end_time', label: 'End Time' },
      { key: 'calendar_id', label: 'Calendar ID' },
      { key: 'location', label: 'Location' },
      { key: 'description', label: 'Description', multiline: true },
      { key: 'attendees', label: 'Attendees', isArray: true }
    ]
  },
  gmail__update_event: {
    displayName: 'Updating Event via Google Calendar',
    fields: [
      { key: 'event_id', label: 'Event ID' },
      { key: 'calendar_id', label: 'Calendar ID' },
      { key: 'title', label: 'Title' },
      { key: 'start_time', label: 'Start Time' },
      { key: 'end_time', label: 'End Time' },
      { key: 'location', label: 'Location' },
      { key: 'attendees', label: 'Attendees', isArray: true }
    ]
  },
  gmail__delete_event: {
    displayName: 'Deleting Event via Google Calendar',
    fields: [
      { key: 'event_id', label: 'Event ID' },
      { key: 'calendar_id', label: 'Calendar ID' },
      { key: 'send_updates', label: 'Send Updates' }
    ]
  },

  // Google Drive Actions
  gmail_drive__get_shared_drives: {
    displayName: 'Getting Shared Drives via Google Drive',
    fields: [{ key: 'page_size', label: 'Page Size' }]
  },
  gmail_drive__get_folders: {
    displayName: 'Getting Folders via Google Drive',
    fields: [
      { key: 'parent_folder_id', label: 'Parent Folder ID' },
      { key: 'name', label: 'Folder Name' },
      { key: 'page_size', label: 'Page Size' }
    ]
  },
  gmail_drive__search_files: {
    displayName: 'Searching Files via Google Drive',
    fields: [
      { key: 'query', label: 'Search Query' },
      { key: 'page_size', label: 'Page Size' }
    ]
  },
  gmail_drive__download_drive_file: {
    displayName: 'Downloading File via Google Drive',
    fields: [{ key: 'item_id', label: 'File ID' }]
  },

  // Slack Actions
  slack__search_messages: {
    displayName: 'Searching Messages via Slack',
    fields: [
      { key: 'query', label: 'Search Query' },
      { key: 'count', label: 'Count' }
    ]
  },
  slack__get_user_by_email: {
    displayName: 'Getting User by Email via Slack',
    fields: [{ key: 'email', label: 'Email' }]
  },
  slack__list_user_conversations: {
    displayName: 'Listing User Conversations via Slack',
    fields: [{ key: 'user_id', label: 'User ID' }]
  },
  slack__get_my_channels: {
    displayName: 'Getting My Channels via Slack',
    fields: [{ key: 'limit', label: 'Limit' }]
  },
  slack__get_channel_info: {
    displayName: 'Getting Channel Info via Slack',
    fields: [{ key: 'channel_id', label: 'Channel ID' }]
  },
  slack__get_channel_messages: {
    displayName: 'Getting Channel Messages via Slack',
    fields: [
      { key: 'channel_id', label: 'Channel ID' },
      { key: 'limit', label: 'Limit' }
    ]
  },
  slack__get_thread_replies: {
    displayName: 'Getting Thread Replies via Slack',
    fields: [
      { key: 'channel_id', label: 'Channel ID' },
      { key: 'thread_ts', label: 'Thread Timestamp' }
    ]
  },
  slack__reply_to_message: {
    displayName: 'Replying to Message via Slack',
    fields: [
      { key: 'channel', label: 'Channel' },
      { key: 'thread_ts', label: 'Thread Timestamp' },
      { key: 'text', label: 'Message', multiline: true }
    ]
  },
  slack__send_message: {
    displayName: 'Sending Message via Slack',
    fields: [
      { key: 'channel', label: 'Channel' },
      { key: 'text', label: 'Message', multiline: true }
    ]
  },

  // HubSpot Actions
  hubspot_mcp_find_companies: {
    displayName: 'Finding Companies via HubSpot',
    fields: [
      { key: 'company_id', label: 'Company ID' },
      { key: 'name', label: 'Name' },
      { key: 'domain', label: 'Domain' }
    ]
  },
  hubspot__create_company: {
    displayName: 'Creating Company via HubSpot',
    fields: [
      { key: 'name', label: 'Name' },
      { key: 'domain', label: 'Domain' },
      { key: 'website', label: 'Website' },
      { key: 'phone', label: 'Phone' },
      { key: 'industry', label: 'Industry' },
      { key: 'address', label: 'Address' },
      { key: 'city', label: 'City' },
      { key: 'state', label: 'State' },
      { key: 'zip', label: 'Postal Code' },
      { key: 'country', label: 'Country' },
      {
        key: 'number_of_employees',
        label: 'Number of Employees'
      },
      { key: 'annual_revenue', label: 'Annual Revenue' },
      {
        key: 'life_cycle_stage',
        label: 'Lifecycle Stage'
      },
      {
        key: 'industry_vertical',
        label: 'Industry Vertical'
      },
      { key: 'industry_group', label: 'Industry Group' },
      {
        key: 'about_us',
        label: 'About Us',
        multiline: true
      },
      {
        key: 'description',
        label: 'Description',
        multiline: true
      }
    ]
  },
  hubspot__update_company: {
    displayName: 'Updating Company via HubSpot',
    fields: [
      { key: 'company_id', label: 'Company ID' },
      { key: 'name', label: 'Name' },
      { key: 'domain', label: 'Domain' },
      { key: 'phone', label: 'Phone' },
      { key: 'industry', label: 'Industry' }
    ]
  },
  hubspot__search_companies: {
    displayName: 'Searching Companies via HubSpot',
    fields: [
      { key: 'property_name', label: 'Property Name' },
      { key: 'filter_value', label: 'Filter Value' },
      { key: 'limit', label: 'Limit' }
    ]
  },
  hubspot__search_companies_advanced: {
    displayName: 'Searching Companies (Advanced) via HubSpot',
    fields: [
      { key: 'filter_groups', label: 'Filter Groups', multiline: true },
      { key: 'limit', label: 'Limit' }
    ]
  },
  hubspot__get_contacts: {
    displayName: 'Getting Contacts via HubSpot',
    fields: [
      { key: 'query', label: 'Search Query' },
      { key: 'limit', label: 'Limit' }
    ]
  },
  hubspot__create_contact: {
    displayName: 'Creating Contact via HubSpot',
    fields: [
      { key: 'email', label: 'Email' },
      { key: 'first_name', label: 'First Name' },
      { key: 'last_name', label: 'Last Name' }
    ]
  },
  hubspot__update_contact: {
    displayName: 'Updating Contact via HubSpot',
    fields: [
      { key: 'contact_id', label: 'Contact ID' },
      { key: 'first_name', label: 'First Name' },
      { key: 'last_name', label: 'Last Name' },
      { key: 'email', label: 'Email' },
      { key: 'phone_number', label: 'Phone Number' },
      { key: 'job_title', label: 'Job Title' }
    ]
  },
  hubspot__create_deal: {
    displayName: 'Creating Deal via HubSpot',
    fields: [{ key: 'create_request', label: 'Deal Details', multiline: true }]
  },
  hubspot__search_deals: {
    displayName: 'Searching Deals via HubSpot',
    fields: [
      { key: 'filter_groups', label: 'Filter Groups', multiline: true },
      { key: 'limit', label: 'Limit' }
    ]
  },
  hubspot__update_deal: {
    displayName: 'Updating Deal via HubSpot',
    fields: [
      { key: 'deal_id', label: 'Deal ID' },
      { key: 'dealname', label: 'Deal Name' },
      { key: 'amount', label: 'Amount' },
      { key: 'closedate', label: 'Close Date' }
    ]
  },
  hubspot__get_company_recent_activities: {
    displayName: 'Getting Company Activities via HubSpot',
    fields: [
      { key: 'company_id', label: 'Company ID' },
      { key: 'activity_type', label: 'Activity Type' }
    ]
  },
  hubspot__get_contact_recent_activities: {
    displayName: 'Getting Contact Activities via HubSpot',
    fields: [
      { key: 'contact_id', label: 'Contact ID' },
      { key: 'activity_type', label: 'Activity Type' }
    ]
  },
  hubspot__create_task: {
    displayName: 'Creating Task via HubSpot',
    fields: [
      { key: 'object_id', label: 'Object ID' },
      { key: 'object_type', label: 'Object Type' },
      { key: 'task_details', label: 'Task Details', multiline: true }
    ]
  },
  hubspot__create_note: {
    displayName: 'Creating Note via HubSpot',
    fields: [
      { key: 'object_id', label: 'Object ID' },
      { key: 'object_type', label: 'Object Type' },
      { key: 'note_details', label: 'Note Details', multiline: true }
    ]
  },

  // Jira Actions
  jira__get_accessible_resources: {
    displayName: 'Getting Accessible Resources via Jira',
    fields: []
  },
  jira__get_current_user: {
    displayName: 'Getting Current User via Jira',
    fields: []
  },
  jira__get_issue: {
    displayName: 'Getting Issue via Jira',
    fields: [
      { key: 'issue_id_or_key', label: 'Issue ID or Key' },
      { key: 'fields', label: 'Fields', isArray: true }
    ]
  },
  jira__get_issue_remote_links: {
    displayName: 'Getting Issue Remote Links via Jira',
    fields: [{ key: 'issue_id_or_key', label: 'Issue ID or Key' }]
  },
  jira__get_issue_type_fields: {
    displayName: 'Getting Issue Type Fields via Jira',
    fields: [
      { key: 'project_id_or_key', label: 'Project ID or Key' },
      { key: 'issue_type_id', label: 'Issue Type ID' }
    ]
  },
  jira__get_project_issue_types: {
    displayName: 'Getting Project Issue Types via Jira',
    fields: [{ key: 'project_id_or_key', label: 'Project ID or Key' }]
  },
  jira__get_issue_link_types: {
    displayName: 'Getting Issue Link Types via Jira',
    fields: []
  },
  jira__get_transitions: {
    displayName: 'Getting Transitions via Jira',
    fields: [{ key: 'issue_id_or_key', label: 'Issue ID or Key' }]
  },
  jira__get_projects: {
    displayName: 'Getting Projects via Jira',
    fields: [{ key: 'search_query', label: 'Search Query' }]
  },
  jira__lookup_account_id: {
    displayName: 'Looking Up Account ID via Jira',
    fields: [{ key: 'search_string', label: 'Name or Email' }]
  },
  jira__add_comment: {
    displayName: 'Adding Comment via Jira',
    fields: [
      { key: 'issue_id_or_key', label: 'Issue ID or Key' },
      { key: 'comment_body', label: 'Comment', multiline: true }
    ]
  },
  jira__add_worklog: {
    displayName: 'Adding Worklog via Jira',
    fields: [
      { key: 'issue_id_or_key', label: 'Issue ID or Key' },
      { key: 'time_spent', label: 'Time Spent (e.g. "3h 30m")' },
      { key: 'comment', label: 'Comment', multiline: true }
    ]
  },
  jira__create_issue: {
    displayName: 'Creating Issue via Jira',
    fields: [
      { key: 'project_key', label: 'Project Key' },
      { key: 'issue_type_name', label: 'Issue Type' },
      { key: 'summary', label: 'Summary' },
      { key: 'description', label: 'Description', multiline: true },
      { key: 'extra_fields', label: 'Extra Fields (JSON)', multiline: true }
    ]
  },
  jira__update_issue: {
    displayName: 'Updating Issue via Jira',
    fields: [
      { key: 'issue_id_or_key', label: 'Issue ID or Key' },
      { key: 'fields', label: 'Fields (JSON)', multiline: true }
    ]
  },
  jira__transition_issue: {
    displayName: 'Transitioning Issue via Jira',
    fields: [
      { key: 'issue_id_or_key', label: 'Issue ID or Key' },
      { key: 'transition_id', label: 'Transition ID' }
    ]
  },
  jira__search_issues: {
    displayName: 'Searching Issues via Jira',
    fields: [
      { key: 'jql', label: 'JQL Query', multiline: true },
      { key: 'max_results', label: 'Max Results' },
      { key: 'fields', label: 'Fields', isArray: true }
    ]
  },

  // Confluence Actions
  confluence__get_accessible_resources: {
    displayName: 'Getting Accessible Resources via Confluence',
    fields: []
  },
  confluence__get_current_user: {
    displayName: 'Getting Current User via Confluence',
    fields: []
  },
  confluence__get_page: {
    displayName: 'Getting Page via Confluence',
    fields: [
      { key: 'page_id', label: 'Page ID' },
      { key: 'body_format', label: 'Body Format (storage or atlas_doc_format)' }
    ]
  },
  confluence__get_page_descendants: {
    displayName: 'Getting Page Descendants via Confluence',
    fields: [
      { key: 'page_id', label: 'Page ID' },
      { key: 'limit', label: 'Limit' }
    ]
  },
  confluence__get_page_footer_comments: {
    displayName: 'Getting Page Footer Comments via Confluence',
    fields: [
      { key: 'page_id', label: 'Page ID' },
      { key: 'limit', label: 'Limit' }
    ]
  },
  confluence__get_page_inline_comments: {
    displayName: 'Getting Page Inline Comments via Confluence',
    fields: [
      { key: 'page_id', label: 'Page ID' },
      { key: 'limit', label: 'Limit' }
    ]
  },
  confluence__get_footer_comment_children: {
    displayName: 'Getting Footer Comment Replies via Confluence',
    fields: [
      { key: 'comment_id', label: 'Comment ID' },
      { key: 'limit', label: 'Limit' }
    ]
  },
  confluence__get_spaces: {
    displayName: 'Getting Spaces via Confluence',
    fields: [{ key: 'limit', label: 'Limit' }]
  },
  confluence__get_pages_in_space: {
    displayName: 'Getting Pages in Space via Confluence',
    fields: [
      { key: 'space_id', label: 'Space ID' },
      { key: 'limit', label: 'Limit' }
    ]
  },
  confluence__create_page: {
    displayName: 'Creating Page via Confluence',
    fields: [
      { key: 'space_id', label: 'Space ID' },
      { key: 'title', label: 'Title' },
      { key: 'body', label: 'Body (storage format HTML/XML)', multiline: true },
      { key: 'parent_id', label: 'Parent Page ID' }
    ]
  },
  confluence__update_page: {
    displayName: 'Updating Page via Confluence',
    fields: [
      { key: 'page_id', label: 'Page ID' },
      { key: 'body', label: 'Body (storage format HTML/XML)', multiline: true },
      { key: 'title', label: 'Title' }
    ]
  },
  confluence__create_footer_comment: {
    displayName: 'Creating Footer Comment via Confluence',
    fields: [
      { key: 'page_id', label: 'Page ID' },
      { key: 'body', label: 'Comment', multiline: true }
    ]
  },
  confluence__create_inline_comment: {
    displayName: 'Creating Inline Comment via Confluence',
    fields: [
      { key: 'page_id', label: 'Page ID' },
      { key: 'body', label: 'Comment', multiline: true },
      { key: 'text_selection', label: 'Anchored Text' }
    ]
  },
  confluence__search_cql: {
    displayName: 'Searching Confluence',
    fields: [
      { key: 'cql', label: 'CQL Query', multiline: true },
      { key: 'limit', label: 'Limit' }
    ]
  },

  // Microsoft Teams Actions
  teams__search_messages: {
    displayName: 'Searching Messages via Teams',
    fields: [
      { key: 'search_query', label: 'Search Query' },
      { key: 'max_results', label: 'Max Results' }
    ]
  },
  teams__list_channels: {
    displayName: 'Listing Channels via Teams',
    fields: []
  },
  teams__get_channel_messages: {
    displayName: 'Getting Channel Messages via Teams',
    fields: [
      { key: 'team_id', label: 'Team ID' },
      { key: 'channel_id', label: 'Channel ID' },
      { key: 'since', label: 'Since Date' },
      { key: 'until', label: 'Until Date' },
      { key: 'max_results', label: 'Max Results' }
    ]
  },
  teams__get_chat_messages: {
    displayName: 'Getting Chat Messages via Teams',
    fields: [
      { key: 'chat_id', label: 'Chat ID' },
      { key: 'since', label: 'Since Date' },
      { key: 'until', label: 'Until Date' },
      { key: 'max_results', label: 'Max Results' }
    ]
  },
  teams__send_channel_message: {
    displayName: 'Sending Channel Message via Teams',
    fields: [
      { key: 'team_id', label: 'Team ID' },
      { key: 'channel_id', label: 'Channel ID' },
      { key: 'content', label: 'Message Content', multiline: true },
      { key: 'content_type', label: 'Content Type' }
    ]
  },
  teams__send_chat_message: {
    displayName: 'Sending Chat Message via Teams',
    fields: [
      { key: 'chat_id', label: 'Chat ID' },
      { key: 'content', label: 'Message Content', multiline: true },
      { key: 'content_type', label: 'Content Type' }
    ]
  },
  teams__list_chats: {
    displayName: 'Listing Chats via Teams',
    fields: [{ key: 'max_results', label: 'Max Results' }]
  },
  teams__get_chat_members: {
    displayName: 'Getting Chat Members via Teams',
    fields: [{ key: 'chat_id', label: 'Chat ID' }]
  },

  // Google Docs Actions
  google_docs__search_docs: {
    displayName: 'Searching Documents via Google Docs',
    fields: [
      { key: 'query', label: 'Search Query' },
      { key: 'owned', label: 'Owned by Me' },
      { key: 'page_size', label: 'Page Size' },
      { key: 'page_token', label: 'Page Token' }
    ]
  },
  google_docs__create_document: {
    displayName: 'Creating Document via Google Docs',
    fields: [{ key: 'title', label: 'Document Title' }]
  },
  google_docs__fetch_document_content: {
    displayName: 'Fetching Document Content via Google Docs',
    fields: [{ key: 'document_id', label: 'Document ID' }]
  },
  google_docs__insert_text: {
    displayName: 'Inserting Text via Google Docs',
    fields: [
      { key: 'document_id', label: 'Document ID' },
      { key: 'inserts', label: 'Text Inserts', multiline: true },
      { key: 'tab_id', label: 'Tab ID' }
    ]
  },
  google_docs__replace_text: {
    displayName: 'Replacing Text via Google Docs',
    fields: [
      { key: 'document_id', label: 'Document ID' },
      { key: 'replacements', label: 'Text Replacements', multiline: true },
      { key: 'tab_id', label: 'Tab ID' }
    ]
  },
  google_docs__format_text: {
    displayName: 'Formatting Text via Google Docs',
    fields: [
      { key: 'document_id', label: 'Document ID' },
      { key: 'formats', label: 'Text Formats', multiline: true },
      { key: 'tab_id', label: 'Tab ID' }
    ]
  },
  google_docs__find_and_replace_all: {
    displayName: 'Finding and Replacing Text via Google Docs',
    fields: [
      { key: 'document_id', label: 'Document ID' },
      { key: 'find_text', label: 'Find Text' },
      { key: 'replace_text', label: 'Replace Text' },
      { key: 'match_case', label: 'Match Case' },
      { key: 'tab_id', label: 'Tab ID' }
    ]
  },

  // Google Sheets Actions
  google_sheets__search_files: {
    displayName: 'Searching Files via Google Sheets',
    fields: [
      { key: 'query', label: 'Search Query' },
      { key: 'owned', label: 'Owned by Me' },
      { key: 'page_size', label: 'Page Size' },
      { key: 'page_token', label: 'Page Token' }
    ]
  },
  google_sheets__create_rows: {
    displayName: 'Creating Rows via Google Sheets',
    fields: [
      { key: 'sheet_name', label: 'Sheet Name' },
      { key: 'spreadsheet_id', label: 'Spreadsheet ID' },
      { key: 'amount', label: 'Number of Rows' },
      { key: 'start_index', label: 'Start Index' }
    ]
  },
  google_sheets__update_rows: {
    displayName: 'Updating Rows via Google Sheets',
    fields: [
      { key: 'spreadsheet_id', label: 'Spreadsheet ID' },
      { key: 'range_notation', label: 'Range Notation' },
      { key: 'values', label: 'Values', multiline: true },
      { key: 'sheet_name', label: 'Sheet Name' },
      { key: 'value_input_option', label: 'Value Input Option' }
    ]
  },
  google_sheets__list_rows: {
    displayName: 'Listing Rows via Google Sheets',
    fields: [
      { key: 'spreadsheet_id', label: 'Spreadsheet ID' },
      { key: 'row_number', label: 'Starting Row Number' },
      { key: 'sheet_name', label: 'Sheet Name' },
      { key: 'max_rows', label: 'Max Rows' }
    ]
  },
  google_sheets__get_metadata: {
    displayName: 'Getting Metadata via Google Sheets',
    fields: [
      { key: 'spreadsheet_id', label: 'Spreadsheet ID' },
      { key: 'include_grid_data', label: 'Include Grid Data' }
    ]
  },
  google_sheets__add_sheet: {
    displayName: 'Adding Sheet via Google Sheets',
    fields: [
      { key: 'spreadsheet_id', label: 'Spreadsheet ID' },
      { key: 'sheet_name', label: 'Sheet Name' },
      { key: 'row_count', label: 'Row Count' },
      { key: 'column_count', label: 'Column Count' }
    ]
  },

  // Microsoft SharePoint Actions
  sharepoint__get_profile: {
    displayName: 'Getting Profile via SharePoint',
    fields: []
  },
  sharepoint__list_drives: {
    displayName: 'Listing Drives via SharePoint',
    fields: [
      { key: 'site_id', label: 'Site ID' },
      { key: 'max_results', label: 'Max Results' },
      { key: 'next_link', label: 'Next Link' }
    ]
  },
  sharepoint__search_sites: {
    displayName: 'Searching Sites via SharePoint',
    fields: [
      { key: 'query', label: 'Search Query' },
      { key: 'max_results', label: 'Max Results' },
      { key: 'next_link', label: 'Next Link' }
    ]
  },
  sharepoint__get_site: {
    displayName: 'Getting Site via SharePoint',
    fields: [{ key: 'site_id', label: 'Site ID' }]
  },
  sharepoint__list_subsites: {
    displayName: 'Listing Subsites via SharePoint',
    fields: [
      { key: 'site_id', label: 'Site ID' },
      { key: 'max_results', label: 'Max Results' },
      { key: 'next_link', label: 'Next Link' }
    ]
  },
  sharepoint__list_drive_children: {
    displayName: 'Listing Drive Children via SharePoint',
    fields: [
      { key: 'drive_id', label: 'Drive ID' },
      { key: 'folder_id', label: 'Folder ID' },
      { key: 'max_results', label: 'Max Results' },
      { key: 'next_link', label: 'Next Link' }
    ]
  },
  sharepoint__list_recent_files: {
    displayName: 'Listing Recent Files via SharePoint',
    fields: [
      { key: 'max_results', label: 'Max Results' },
      { key: 'next_link', label: 'Next Link' }
    ]
  },
  sharepoint__list_site_users: {
    displayName: 'Listing Site Users via SharePoint',
    fields: [
      { key: 'site_id', label: 'Site ID' },
      { key: 'max_results', label: 'Max Results' },
      { key: 'next_link', label: 'Next Link' }
    ]
  },
  sharepoint__list_site_lists: {
    displayName: 'Listing Site Lists via SharePoint',
    fields: [
      { key: 'site_id', label: 'Site ID' },
      { key: 'max_results', label: 'Max Results' },
      { key: 'next_link', label: 'Next Link' }
    ]
  },
  sharepoint__get_list: {
    displayName: 'Getting List via SharePoint',
    fields: [
      { key: 'site_id', label: 'Site ID' },
      { key: 'list_id', label: 'List ID' }
    ]
  },
  sharepoint__list_columns: {
    displayName: 'Listing Columns via SharePoint',
    fields: [
      { key: 'site_id', label: 'Site ID' },
      { key: 'list_id', label: 'List ID' },
      { key: 'max_results', label: 'Max Results' },
      { key: 'next_link', label: 'Next Link' }
    ]
  },
  sharepoint__get_column: {
    displayName: 'Getting Column via SharePoint',
    fields: [
      { key: 'site_id', label: 'Site ID' },
      { key: 'list_id', label: 'List ID' },
      { key: 'column_id', label: 'Column ID' }
    ]
  },
  sharepoint__list_list_items: {
    displayName: 'Listing List Items via SharePoint',
    fields: [
      { key: 'site_id', label: 'Site ID' },
      { key: 'list_id', label: 'List ID' },
      { key: 'filter_query', label: 'Filter Query' },
      { key: 'max_results', label: 'Max Results' },
      { key: 'next_link', label: 'Next Link' }
    ]
  },
  sharepoint__get_list_item: {
    displayName: 'Getting List Item via SharePoint',
    fields: [
      { key: 'site_id', label: 'Site ID' },
      { key: 'list_id', label: 'List ID' },
      { key: 'list_item_id', label: 'List Item ID' }
    ]
  },
  sharepoint__list_site_pages: {
    displayName: 'Listing Site Pages via SharePoint',
    fields: [
      { key: 'site_id', label: 'Site ID' },
      { key: 'max_results', label: 'Max Results' },
      { key: 'next_link', label: 'Next Link' }
    ]
  },
  sharepoint__get_groups: {
    displayName: 'Getting Groups via SharePoint',
    fields: [
      { key: 'user_id', label: 'User ID' },
      { key: 'max_results', label: 'Max Results' },
      { key: 'next_link', label: 'Next Link' }
    ]
  },
  sharepoint__get_group_members: {
    displayName: 'Getting Group Members via SharePoint',
    fields: [
      { key: 'group_id', label: 'Group ID' },
      { key: 'max_results', label: 'Max Results' },
      { key: 'next_link', label: 'Next Link' }
    ]
  },
  sharepoint__add_group_member: {
    displayName: 'Adding Group Member via SharePoint',
    fields: [
      { key: 'group_id', label: 'Group ID' },
      { key: 'member_id', label: 'Member ID' }
    ]
  },
  sharepoint__remove_group_member: {
    displayName: 'Removing Group Member via SharePoint',
    fields: [
      { key: 'group_id', label: 'Group ID' },
      { key: 'member_id', label: 'Member ID' }
    ]
  },
  sharepoint__search_files: {
    displayName: 'Searching Files via SharePoint',
    fields: [
      { key: 'query', label: 'Search Query' },
      { key: 'drive_id', label: 'Drive ID' },
      { key: 'max_results', label: 'Max Results' },
      { key: 'next_link', label: 'Next Link' }
    ]
  },
  sharepoint__upload_file: {
    displayName: 'Uploading File via SharePoint',
    fields: [
      { key: 'drive_id', label: 'Drive ID' },
      { key: 'file_path', label: 'File Path' },
      { key: 'folder_id', label: 'Folder ID' },
      { key: 'file_name', label: 'File Name' }
    ]
  },
  sharepoint__get_folder: {
    displayName: 'Getting Folder via SharePoint',
    fields: [
      { key: 'drive_id', label: 'Drive ID' },
      { key: 'folder_path', label: 'Folder Path' }
    ]
  },

  // Microsoft OneDrive Actions
  onedrive__list_available_drives: {
    displayName: 'Listing Available Drives via OneDrive',
    fields: [
      { key: 'max_results', label: 'Max Results' },
      { key: 'next_link', label: 'Next Link' }
    ]
  },
  onedrive__get_folder_by_path: {
    displayName: 'Getting Folder by Path via OneDrive',
    fields: [
      { key: 'folder_path', label: 'Folder Path' },
      { key: 'drive_id', label: 'Drive ID' }
    ]
  },
  onedrive__search_folders_by_name: {
    displayName: 'Searching Folders by Name via OneDrive',
    fields: [
      { key: 'folder_name', label: 'Folder Name' },
      { key: 'max_results', label: 'Max Results' },
      { key: 'drive_id', label: 'Drive ID' },
      { key: 'next_link', label: 'Next Link' }
    ]
  },
  onedrive__search_files_by_name: {
    displayName: 'Searching Files by Name via OneDrive',
    fields: [
      { key: 'file_name', label: 'File Name' },
      { key: 'max_results', label: 'Max Results' },
      { key: 'drive_id', label: 'Drive ID' },
      { key: 'next_link', label: 'Next Link' }
    ]
  },
  onedrive__create_folder: {
    displayName: 'Creating Folder via OneDrive',
    fields: [
      { key: 'folder_path', label: 'Folder Path' },
      { key: 'drive_id', label: 'Drive ID' }
    ]
  },
  onedrive__list_folder_children: {
    displayName: 'Listing Folder Children via OneDrive',
    fields: [
      { key: 'folder_path', label: 'Folder Path' },
      { key: 'max_results', label: 'Max Results' },
      { key: 'drive_id', label: 'Drive ID' },
      { key: 'next_link', label: 'Next Link' }
    ]
  },
  onedrive__download_file: {
    displayName: 'Downloading File via OneDrive',
    fields: [
      { key: 'file_id', label: 'File ID' },
      { key: 'drive_id', label: 'Drive ID' }
    ]
  },

  // DATEV Accounting Actions
  datev__list_clients: {
    displayName: 'List DATEV Clients',
    fields: []
  },
  datev__get_client: {
    displayName: 'Get DATEV Client',
    fields: [
      { key: 'client_id', label: 'Client ID' }
    ]
  },
  datev__get_fiscal_years: {
    displayName: 'Get DATEV Fiscal Years',
    fields: [
      { key: 'client_id', label: 'Client ID' }
    ]
  },
  datev__get_fiscal_year: {
    displayName: 'Get DATEV Fiscal Year',
    fields: [
      { key: 'client_id', label: 'Client ID' },
      { key: 'fiscal_year_id', label: 'Fiscal Year ID (e.g. 20240101)' }
    ]
  },
  datev__get_general_ledger_accounts: {
    displayName: 'Get DATEV General Ledger Accounts',
    fields: [
      { key: 'client_id', label: 'Client ID' },
      { key: 'fiscal_year_id', label: 'Fiscal Year ID (e.g. 20240101)' },
      { key: 'only_booked', label: 'Only Booked (true/false)' },
      { key: 'includes_personal_accounts', label: 'Include Personal Accounts (true/false)' }
    ]
  },
  datev__get_sums_and_balances: {
    displayName: 'Get DATEV Sums and Balances',
    fields: [
      { key: 'client_id', label: 'Client ID' },
      { key: 'fiscal_year_id', label: 'Fiscal Year ID (e.g. 20240101)' }
    ]
  },
  datev__get_accounting_sequences: {
    displayName: 'Get DATEV Accounting Sequences',
    fields: [
      { key: 'client_id', label: 'Client ID' },
      { key: 'fiscal_year_id', label: 'Fiscal Year ID (e.g. 20240101)' }
    ]
  },
  datev__get_terms_of_payment: {
    displayName: 'Get DATEV Terms of Payment',
    fields: [
      { key: 'client_id', label: 'Client ID' },
      { key: 'fiscal_year_id', label: 'Fiscal Year ID (e.g. 20240101)' }
    ]
  },
  datev__get_document_types: {
    displayName: 'Get DATEV Document Types',
    fields: [
      { key: 'client_id', label: 'Client ID' }
    ]
  },
  datev__get_duo_version: {
    displayName: 'Get DATEV DUO Version',
    fields: [
      { key: 'client_id', label: 'Client ID' }
    ]
  },

  // DATEV HR Actions
  datev__list_hr_clients: {
    displayName: 'List DATEV HR Clients',
    fields: []
  },
  datev__get_hr_client: {
    displayName: 'Get DATEV HR Client',
    fields: [
      { key: 'client_id', label: 'Client ID' }
    ]
  },
  datev__get_employee_master_data: {
    displayName: 'Get DATEV Employee Master Data',
    fields: [
      { key: 'client_id', label: 'Client ID' },
      { key: 'employee_id', label: 'Employee ID' },
      { key: 'payroll_recalculation_month', label: 'Payroll Recalculation Month (YYYY-MM, optional)' },
      { key: 'payroll_recalculation_month_end', label: 'Payroll Recalculation Month End (YYYY-MM, optional)' },
      { key: 'payroll_accounting_month', label: 'Payroll Accounting Month (YYYY-MM, optional)' },
      { key: 'payroll_accounting_month_end', label: 'Payroll Accounting Month End (YYYY-MM, optional)' }
    ]
  },
  datev__get_employee_salary_details: {
    displayName: 'Get DATEV Employee Salary Details',
    fields: [
      { key: 'client_id', label: 'Client ID' },
      { key: 'employee_id', label: 'Employee ID' },
      { key: 'payroll_recalculation_month', label: 'Payroll Recalculation Month (YYYY-MM, optional)' },
      { key: 'payroll_recalculation_month_end', label: 'Payroll Recalculation Month End (YYYY-MM, optional)' },
      { key: 'payroll_accounting_month', label: 'Payroll Accounting Month (YYYY-MM, optional)' },
      { key: 'payroll_accounting_month_end', label: 'Payroll Accounting Month End (YYYY-MM, optional)' }
    ]
  },
  datev__get_employee_tax_payments: {
    displayName: 'Get DATEV Employee Tax Payments',
    fields: [
      { key: 'client_id', label: 'Client ID' },
      { key: 'employee_id', label: 'Employee ID' },
      { key: 'payroll_recalculation_month', label: 'Payroll Recalculation Month (YYYY-MM, optional)' },
      { key: 'payroll_recalculation_month_end', label: 'Payroll Recalculation Month End (YYYY-MM, optional)' },
      { key: 'payroll_accounting_month', label: 'Payroll Accounting Month (YYYY-MM, optional)' },
      { key: 'payroll_accounting_month_end', label: 'Payroll Accounting Month End (YYYY-MM, optional)' }
    ]
  },
  datev__get_employee_social_security_payments: {
    displayName: 'Get DATEV Employee Social Security Payments',
    fields: [
      { key: 'client_id', label: 'Client ID' },
      { key: 'employee_id', label: 'Employee ID' },
      { key: 'payroll_recalculation_month', label: 'Payroll Recalculation Month (YYYY-MM, optional)' },
      { key: 'payroll_recalculation_month_end', label: 'Payroll Recalculation Month End (YYYY-MM, optional)' },
      { key: 'payroll_accounting_month', label: 'Payroll Accounting Month (YYYY-MM, optional)' },
      { key: 'payroll_accounting_month_end', label: 'Payroll Accounting Month End (YYYY-MM, optional)' }
    ]
  },
  datev__get_employee_absences: {
    displayName: 'Get DATEV Employee Absences',
    fields: [
      { key: 'client_id', label: 'Client ID' },
      { key: 'employee_id', label: 'Employee ID' },
      { key: 'payroll_recalculation_month', label: 'Payroll Recalculation Month (YYYY-MM, optional)' },
      { key: 'payroll_recalculation_month_end', label: 'Payroll Recalculation Month End (YYYY-MM, optional)' },
      { key: 'payroll_accounting_month', label: 'Payroll Accounting Month (YYYY-MM, optional)' },
      { key: 'payroll_accounting_month_end', label: 'Payroll Accounting Month End (YYYY-MM, optional)' }
    ]
  },
  datev__get_employee_id_by_company_personnel_number: {
    displayName: 'Get DATEV Employee ID by Personnel Number',
    fields: [
      { key: 'client_id', label: 'Client ID' },
      { key: 'company_personnel_number', label: 'Company Personnel Number' }
    ]
  },
  datev__get_all_employees_master_data: {
    displayName: 'Get DATEV All Employees Master Data',
    fields: [
      { key: 'client_id', label: 'Client ID' },
      { key: 'payroll_recalculation_month', label: 'Payroll Recalculation Month (YYYY-MM, optional)' },
      { key: 'payroll_recalculation_month_end', label: 'Payroll Recalculation Month End (YYYY-MM, optional)' },
      { key: 'payroll_accounting_month', label: 'Payroll Accounting Month (YYYY-MM, optional)' },
      { key: 'payroll_accounting_month_end', label: 'Payroll Accounting Month End (YYYY-MM, optional)' }
    ]
  },
  datev__get_all_employees_salary_details: {
    displayName: 'Get DATEV All Employees Salary Details',
    fields: [
      { key: 'client_id', label: 'Client ID' },
      { key: 'payroll_recalculation_month', label: 'Payroll Recalculation Month (YYYY-MM, optional)' },
      { key: 'payroll_recalculation_month_end', label: 'Payroll Recalculation Month End (YYYY-MM, optional)' },
      { key: 'payroll_accounting_month', label: 'Payroll Accounting Month (YYYY-MM, optional)' },
      { key: 'payroll_accounting_month_end', label: 'Payroll Accounting Month End (YYYY-MM, optional)' }
    ]
  },
  datev__get_all_employees_tax_payments: {
    displayName: 'Get DATEV All Employees Tax Payments',
    fields: [
      { key: 'client_id', label: 'Client ID' },
      { key: 'payroll_recalculation_month', label: 'Payroll Recalculation Month (YYYY-MM, optional)' },
      { key: 'payroll_recalculation_month_end', label: 'Payroll Recalculation Month End (YYYY-MM, optional)' },
      { key: 'payroll_accounting_month', label: 'Payroll Accounting Month (YYYY-MM, optional)' },
      { key: 'payroll_accounting_month_end', label: 'Payroll Accounting Month End (YYYY-MM, optional)' }
    ]
  },
  datev__get_all_employees_social_security_payments: {
    displayName: 'Get DATEV All Employees Social Security Payments',
    fields: [
      { key: 'client_id', label: 'Client ID' },
      { key: 'payroll_recalculation_month', label: 'Payroll Recalculation Month (YYYY-MM, optional)' },
      { key: 'payroll_recalculation_month_end', label: 'Payroll Recalculation Month End (YYYY-MM, optional)' },
      { key: 'payroll_accounting_month', label: 'Payroll Accounting Month (YYYY-MM, optional)' },
      { key: 'payroll_accounting_month_end', label: 'Payroll Accounting Month End (YYYY-MM, optional)' }
    ]
  },
  datev__get_all_employees_absences: {
    displayName: 'Get DATEV All Employees Absences',
    fields: [
      { key: 'client_id', label: 'Client ID' },
      { key: 'payroll_recalculation_month', label: 'Payroll Recalculation Month (YYYY-MM, optional)' },
      { key: 'payroll_recalculation_month_end', label: 'Payroll Recalculation Month End (YYYY-MM, optional)' },
      { key: 'payroll_accounting_month', label: 'Payroll Accounting Month (YYYY-MM, optional)' },
      { key: 'payroll_accounting_month_end', label: 'Payroll Accounting Month End (YYYY-MM, optional)' }
    ]
  },
  datev__get_payroll_client: {
    displayName: 'Get DATEV Payroll Client',
    fields: [
      { key: 'client_id', label: 'Client ID' }
    ]
  },
  datev__get_payroll_reports: {
    displayName: 'Get DATEV Payroll Reports',
    fields: [
      { key: 'client_id', label: 'Client ID' },
      { key: 'period', label: 'Period (YYYY-MM-DD, e.g. 2024-09-01)' },
      { key: 'document_types', label: 'Document Types (e.g. LOBN, comma-separated)' }
    ]
  },
  datev__get_payroll_document_availability: {
    displayName: 'Get DATEV Payroll Document Availability',
    fields: [
      { key: 'client_id', label: 'Client ID' },
      { key: 'period', label: 'Period (YYYY-MM-DD)' }
    ]
  },
  datev__get_employee_payslip_numbers: {
    displayName: 'Get DATEV Employee Payslip Numbers',
    fields: [
      { key: 'client_id', label: 'Client ID' },
      { key: 'period', label: 'Period (YYYY-MM-DD)' },
      { key: 'document_types', label: 'Document Types (optional, comma-separated)' }
    ]
  },
  datev__get_payroll_report_metadata: {
    displayName: 'Get DATEV Payroll Report Metadata',
    fields: [
      { key: 'client_id', label: 'Client ID' },
      { key: 'period', label: 'Period (YYYY-MM-DD, optional)' },
      { key: 'document_types', label: 'Document Types (optional, comma-separated)' }
    ]
  },
  
  // SQL Database Actions
  sql_database__execute_sql: {
    displayName: 'Executing SQL Query via SQL Database',
    fields: [{ key: 'sql', label: 'SQL', multiline: true }]
  },
  sql_database__search_objects: {
    displayName: 'Searching Database Objects via SQL Database',
    fields: [
      {
        key: 'object_type',
        label: 'Object Type (schema, table, column, procedure, function, index)'
      },
      {
        key: 'pattern',
        label: 'Pattern (SQL LIKE: % wildcard, _ single char; default %)'
      },
      { key: 'schema', label: 'Schema (optional filter)' },
      { key: 'detail_level', label: 'Detail Level (names, summary, full)' },
      { key: 'limit', label: 'Limit (default 100, max 1000)' }
    ]
  },

  // Dynamics 365 Actions
  d365__list_customers: {
    displayName: 'Listing Customers via Dynamics 365',
    fields: MICROSOFT_D365_QUERY_FIELDS
  },
  d365__list_vendors: {
    displayName: 'Listing Vendors via Dynamics 365',
    fields: MICROSOFT_D365_QUERY_FIELDS
  },
  d365__list_items: {
    displayName: 'Listing Items via Dynamics 365',
    fields: MICROSOFT_D365_QUERY_FIELDS
  },
  d365__list_employees: {
    displayName: 'Listing Employees via Dynamics 365',
    fields: MICROSOFT_D365_QUERY_FIELDS
  },
  d365__list_company_information: {
    displayName: 'Listing Company Information via Dynamics 365',
    fields: MICROSOFT_D365_QUERY_FIELDS
  },
  d365__list_bank_accounts: {
    displayName: 'Listing Bank Accounts via Dynamics 365',
    fields: MICROSOFT_D365_QUERY_FIELDS
  },
  d365__list_currencies: {
    displayName: 'Listing Currencies via Dynamics 365',
    fields: MICROSOFT_D365_QUERY_FIELDS
  },
  d365__list_sales_invoices: {
    displayName: 'Listing Sales Invoices via Dynamics 365',
    fields: MICROSOFT_D365_QUERY_FIELDS
  },
  d365__list_posted_sales_invoices: {
    displayName: 'Listing Posted Sales Invoices via Dynamics 365',
    fields: MICROSOFT_D365_QUERY_FIELDS
  },
  d365__list_sales_credit_memos: {
    displayName: 'Listing Sales Credit Memos via Dynamics 365',
    fields: MICROSOFT_D365_QUERY_FIELDS
  },
  d365__list_sales_orders: {
    displayName: 'Listing Sales Orders via Dynamics 365',
    fields: MICROSOFT_D365_QUERY_FIELDS
  },
  d365__list_customer_payments: {
    displayName: 'Listing Customer Payments via Dynamics 365',
    fields: MICROSOFT_D365_QUERY_FIELDS
  },
  d365__list_purchase_invoices: {
    displayName: 'Listing Purchase Invoices via Dynamics 365',
    fields: MICROSOFT_D365_QUERY_FIELDS
  },
  d365__list_posted_purchase_invoices: {
    displayName: 'Listing Posted Purchase Invoices via Dynamics 365',
    fields: MICROSOFT_D365_QUERY_FIELDS
  },
  d365__list_purchase_credit_memos: {
    displayName: 'Listing Purchase Credit Memos via Dynamics 365',
    fields: MICROSOFT_D365_QUERY_FIELDS
  },
  d365__list_purchase_orders: {
    displayName: 'Listing Purchase Orders via Dynamics 365',
    fields: MICROSOFT_D365_QUERY_FIELDS
  },
  d365__list_vendor_payments: {
    displayName: 'Listing Vendor Payments via Dynamics 365',
    fields: MICROSOFT_D365_QUERY_FIELDS
  },
  d365__list_gl_accounts: {
    displayName: 'Listing GL Accounts via Dynamics 365',
    fields: MICROSOFT_D365_QUERY_FIELDS
  },
  d365__list_general_ledger_entries: {
    displayName: 'Listing General Ledger Entries via Dynamics 365',
    fields: MICROSOFT_D365_QUERY_FIELDS
  },
  d365__list_trial_balances: {
    displayName: 'Listing Trial Balances via Dynamics 365',
    fields: MICROSOFT_D365_QUERY_FIELDS
  }
};
